import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  COOKIE_CONSENT_KEY,
  COOKIE_CONSENT_CATS_KEY,
} from '@/lib/cookie-consent';

/**
 * GDPR-05 / D-37 / CNIL: Cookie consent banner — Shadcn Sheet rebuild.
 *
 * Renders on first visit (localStorage.getItem('cookie-consent') === null).
 * Persists 'reject' or 'accept' in localStorage on button click.
 * Both buttons use IDENTICAL Button variant + className 'cookie-btn' — no asymmetry.
 *
 * The Sheet is non-modal so the user can keep reading the page while undecided.
 * onInteractOutside is prevented so the Sheet does not auto-dismiss without choice.
 *
 * Customize Dialog exposes per-category Switch toggles. Functional cookies are
 * always-on/disabled (strictly necessary). Analytics + marketing default off.
 *
 * localStorage keys (preserved verbatim from Phase 1 contract):
 *   'cookie-consent'      → 'accept' | 'reject'
 *   'cookie-consent-cats' → JSON.stringify({ analytics: bool, marketing: bool })
 */
type Categories = { analytics: boolean; marketing: boolean };

export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [cats, setCats] = useState<Categories>({ analytics: false, marketing: false });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existing = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (existing === null) setOpen(true);
  }, []);

  const persist = (decision: 'accept' | 'reject', categories: Categories) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, decision);
    localStorage.setItem(COOKIE_CONSENT_CATS_KEY, JSON.stringify(categories));
    setOpen(false);
    setCustomizeOpen(false);
  };

  const rejectAll = () => persist('reject', { analytics: false, marketing: false });
  const acceptAll = () => persist('accept', { analytics: true, marketing: true });
  const saveChoices = () => persist('accept', cats);

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen} modal={false}>
        <SheetContent
          side="bottom"
          className="rounded-t-[var(--radius)]"
          onInteractOutside={(e) => e.preventDefault()}
          data-testid="cookie-consent-banner"
          aria-label={t('gdpr.consent.aria_label')}
          noOverlay
        >
          <SheetHeader>
            <SheetTitle>{t('gdpr.consent.title')}</SheetTitle>
            <SheetDescription>
              {t('gdpr.consent.body_prefix')}{' '}
              <a href="/legal" className="underline">
                {t('gdpr.consent.learn_more')}
              </a>
              .
            </SheetDescription>
            {/* Legal reachability while the banner is open: header nav drops
                imprint + privacy, so surface them here so a user can read
                both before consenting. */}
            <div className="text-xs text-muted-foreground flex gap-3 pt-1">
              <a href="/imprint" className="underline">
                {t('gdpr.consent.imprint')}
              </a>
              <span aria-hidden="true">·</span>
              <a href="/legal" className="underline">
                {t('gdpr.consent.privacy')}
              </a>
            </div>
          </SheetHeader>
          {/* GDPR-05 / CNIL: identical variant + className on both buttons — no prominence asymmetry. */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="default" className="cookie-btn" onClick={rejectAll}>
              {t('gdpr.consent.reject_all')}
            </Button>
            <Button variant="default" className="cookie-btn" onClick={acceptAll}>
              {t('gdpr.consent.accept_all')}
            </Button>
            <Button variant="ghost" onClick={() => setCustomizeOpen(true)}>
              {t('gdpr.consent.customize')}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('gdpr.consent.customize_title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="cookie-cat-functional">
                  {t('gdpr.consent.cat_functional')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('gdpr.consent.cat_functional_hint')}
                </p>
              </div>
              <Switch id="cookie-cat-functional" checked disabled />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="cookie-cat-analytics">
                  {t('gdpr.consent.cat_analytics')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('gdpr.consent.cat_analytics_hint')}
                </p>
              </div>
              <Switch
                id="cookie-cat-analytics"
                checked={cats.analytics}
                onCheckedChange={(v) => setCats((c) => ({ ...c, analytics: v }))}
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="cookie-cat-marketing">
                  {t('gdpr.consent.cat_marketing')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('gdpr.consent.cat_marketing_hint')}
                </p>
              </div>
              <Switch
                id="cookie-cat-marketing"
                checked={cats.marketing}
                onCheckedChange={(v) => setCats((c) => ({ ...c, marketing: v }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="default" onClick={saveChoices}>
              {t('gdpr.consent.save_choices')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
